import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "flex items-center justify-between py-3.5 px-8 bg-primary-color-600"
}
const _hoisted_2 = { class: "flex items-center gap-x-3" }
const _hoisted_3 = { class: "text-white font-medium" }
const _hoisted_4 = { class: "flex gap-x-4" }
const _hoisted_5 = { class: "w-[330px] absolute z-50 -left-48 rounded-md shadow-lg bg-white p-4 ring-1 ring-black ring-opacity-5" }
const _hoisted_6 = { class: "text-sm text-gray-500 mb-4" }
const _hoisted_7 = { class: "flex justify-between mb-4" }
const _hoisted_8 = { class: "flex gap-4" }
const _hoisted_9 = { class: "flex flex-col items-center justify-center" }
const _hoisted_10 = { class: "text-sm font-semibold text-white" }

import { computed, onMounted, ref, watch } from "vue"
import GgmsButton from "@/components/GgmsButton.vue"
import GgmsGeneralAgent from "@/components/GgmsGeneralAgent.vue"
import {
    CalendarIcon,
    RewindIcon,
    CheckCircleIcon,
    XIcon,
    ClockIcon,
    ArrowCircleRightIcon,
} from "@heroicons/vue/outline"
import { useTaskStore } from "@/stores/task"
import { Task } from "@/shared/models/task"
import { getInitials } from "@/shared/utils/helpers"
import { formatters, getters, predicates } from "@/shared/utils/dateUtils"
import { ToastService } from "@/shared/services/toast"
import GgmsCalendar from "@/components/GgmsCalendar.vue"


export default /*@__PURE__*/_defineComponent({
  __name: 'GgmsTaskSessionBar',
  setup(__props) {

const taskStore = useTaskStore()
const toastService = new ToastService()

const activeTasks = computed(() => taskStore.activeTasks)
const activeTask = computed(() => taskStore.activeTask)

const activeTaskIndex = computed(() => activeTasks.value.findIndex((task) => task._id === activeTask.value?._id))
const fullNameInitiales = computed(() => getInitials(activeTask?.value?.agent?.fullName || "G G"))
const isPreviousActive = computed(() => getPreviousPendingTask() !== -1)
const isNextActive = computed(() => getNextPendingTask() !== -1)

const isSelectedDateToday = ref(false)
const minHour = computed(() => (isSelectedDateToday.value ? "now" : "today"))
const updatedActiveTask = ref<Task>({ ...taskStore.activeTask })

const activeTaskDate = computed(() =>
    formatters.formatHumanReadableDate(new Date(activeTask.value.when?.startAt || ""), false)
)
const newTaskDate = ref(getters.getDateInputFormat(new Date()))
const newTaskTime = ref(getters.getTimeInputFormat(new Date()))

const rescheduleOverlayPanel = ref()
const isDropdownVisible = ref(false)

function toggleReschedulePanel(event?: Event) {
    updatedActiveTask.value = { ...activeTask.value }
    setTaskStartDate()
    rescheduleOverlayPanel.value.toggle(event)
    isDropdownVisible.value = !isDropdownVisible.value
}

function setDropdownVisibility(isVisible: boolean) {
    isDropdownVisible.value = isVisible
    if (isVisible) {
        document.querySelectorAll(".flatpickr-calendar").forEach((el) => {
            ;(el as HTMLElement).onclick = (event) => event.stopPropagation()
        })
    }
}

function setTaskStartDate() {
    const date = new Date(updatedActiveTask.value.when.startAt)
    const isoDateInAgencyTimezone = formatters.formatDateToIso(date)
    const [datePart, timePart] = isoDateInAgencyTimezone.split("T")
    newTaskDate.value = datePart
    newTaskTime.value = timePart.slice(0, 5)

    isSelectedDateToday.value = predicates.isToday(date)
}

async function rescheduleTask() {
    updatedActiveTask.value = { ...activeTask.value }
    const date = new Date(newTaskDate.value)
    const [hours, minutes] = getters.getHoursAndMinutes(newTaskTime.value)
    const newDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), hours, minutes)

    updatedActiveTask.value.when = {
        ...updatedActiveTask.value?.when,
        startAt: formatters.formatDateToUtc(newDate),
    }
    delete updatedActiveTask.value.agent
    updatedActiveTask.value.agentId = activeTask.value.agent?._id

    await taskStore.updateTask(activeTask.value._id || "", updatedActiveTask.value)

    toggleReschedulePanel()
    toastService.addToast({
        type: "success",
        message: "Task rescheduled.",
    })

    await Promise.all([taskStore.getTasks(), taskStore.getActiveTaskSession()])
    const currentTaskIndex = taskStore.tableState.data.findIndex((task) => task._id === activeTask.value._id)
    const tasksAfterCurrent = taskStore.tableState.data.slice(currentTaskIndex + 1)
    const pendingTasksAfterCurrent = tasksAfterCurrent.filter((task) => task.status === "pending")

    if (pendingTasksAfterCurrent.length === 0) {
        goToFirstPendingTask()
    }

    if (activeTask.value.status === "completed") {
        goToNextTask()
    }
}

async function completeTask() {
    taskStore.activeTask.status = "completed"
    const completedTask: Partial<Task> = { ...activeTask.value }
    delete completedTask.agent
    delete completedTask._id
    completedTask.agentId = activeTask.value.agent?._id

    await taskStore.updateTask(activeTask.value._id || "", completedTask as Task)

    toastService.addToast({
        type: "success",
        message: "Task successfully completed!",
    })

    if (taskStore.checkIfAllActiveTasksCompleted()) {
        await taskStore.closeTaskSession()
    }
    goToPendingTask()
}

function goToPendingTask() {
    isNextActive.value ? goToNextTask() : goToFirstPendingTask()
}

function goToFirstPendingTask() {
    const firstPendingTask = activeTasks.value.find((task) => task.status === "pending")
    if (firstPendingTask) {
        taskStore.activeTask = firstPendingTask
    }
}

function goBackToPreviousTask() {
    const previousPendingTaskIndex = getPreviousPendingTask()
    if (previousPendingTaskIndex !== -1) {
        taskStore.activeTask = activeTasks.value[previousPendingTaskIndex]
    }
}

function getPreviousPendingTask(): number {
    return activeTasks.value
        .map((task, index) => task.status === "pending" && index < activeTaskIndex.value)
        .lastIndexOf(true)
}

async function goToNextTask() {
    const nextPendingTaskIndex = getNextPendingTask()
    if (nextPendingTaskIndex !== -1) {
        taskStore.activeTask = activeTasks.value[nextPendingTaskIndex]
    }
}

function getNextPendingTask(): number {
    return activeTasks.value.findIndex((task, index) => task.status === "pending" && index > activeTaskIndex.value)
}

function checkDate() {
    if (newTaskDate.value && newTaskTime.value && predicates.isEarlierToday(newTaskDate.value, newTaskTime.value)) {
        const newDate = new Date()
        newTaskTime.value = `${newDate.getHours()}:${newDate.getMinutes() < 10 ? "0" : ""}${newDate.getMinutes()}`
    }
}

onMounted(async () => {
    await taskStore.getActiveTaskSession()

    if (!activeTask.value?._id) {
        await taskStore.closeTaskSession()
    }
})

watch(
    () => newTaskDate.value,
    () => {
        const date = new Date(newTaskDate.value)
        isSelectedDateToday.value = predicates.isToday(date)
    }
)

watch(activeTaskIndex, () => {
    sessionStorage.setItem("savedActiveTaskIndex", activeTaskIndex.value.toString())
})

return (_ctx: any,_cache: any) => {
  const _component_Avatar = _resolveComponent("Avatar")!
  const _component_OverlayPanel = _resolveComponent("OverlayPanel")!

  return (activeTask.value)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_component_Avatar, {
            shape: "circle",
            label: fullNameInitiales.value
          }, null, 8, ["label"]),
          _createVNode(GgmsGeneralAgent, {
            agent: activeTask.value.agent,
            class: "!text-base text-white font-medium pr-3 border-r border-white"
          }, null, 8, ["agent"]),
          _createElementVNode("p", _hoisted_3, "Task Title: " + _toDisplayString(activeTask.value.displayName), 1)
        ]),
        _createElementVNode("div", _hoisted_4, [
          _createVNode(GgmsButton, {
            styleType: "primary",
            iconStart: _unref(CalendarIcon),
            onClick: toggleReschedulePanel
          }, {
            default: _withCtx(() => _cache[5] || (_cache[5] = [
              _createTextVNode(" Reschedule ")
            ])),
            _: 1,
            __: [5]
          }, 8, ["iconStart"]),
          _createVNode(_component_OverlayPanel, {
            ref_key: "rescheduleOverlayPanel",
            ref: rescheduleOverlayPanel,
            onHide: _cache[2] || (_cache[2] = ($event: any) => (setDropdownVisibility(false))),
            onShow: _cache[3] || (_cache[3] = ($event: any) => (setDropdownVisibility(true)))
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_5, [
                _cache[8] || (_cache[8] = _createElementVNode("p", { class: "text-sm text-gray-900 mb-1" }, "Current Due Date", -1)),
                _createElementVNode("p", _hoisted_6, _toDisplayString(activeTaskDate.value), 1),
                _cache[9] || (_cache[9] = _createElementVNode("p", { class: "text-sm text-gray-700 mb-1" }, "New Due Date", -1)),
                _createElementVNode("div", _hoisted_7, [
                  _createVNode(GgmsCalendar, {
                    modelValue: newTaskDate.value,
                    "onUpdate:modelValue": [
                      _cache[0] || (_cache[0] = ($event: any) => ((newTaskDate).value = $event)),
                      checkDate
                    ],
                    position: "below",
                    showTime: false,
                    showDate: true,
                    minDate: "today",
                    small: true,
                    iconStart: _unref(CalendarIcon),
                    class: "w-[150px]"
                  }, null, 8, ["modelValue", "iconStart"]),
                  _createVNode(GgmsCalendar, {
                    modelValue: newTaskTime.value,
                    "onUpdate:modelValue": [
                      _cache[1] || (_cache[1] = ($event: any) => ((newTaskTime).value = $event)),
                      checkDate
                    ],
                    position: "below",
                    showTime: true,
                    showDate: false,
                    iconStart: _unref(ClockIcon),
                    minDate: minHour.value,
                    class: "w-[132px]"
                  }, null, 8, ["modelValue", "iconStart", "minDate"])
                ]),
                _createElementVNode("div", _hoisted_8, [
                  _createVNode(GgmsButton, {
                    styleType: "simple",
                    classes: ['py-0 font-normal px-0'],
                    onClick: rescheduleTask
                  }, {
                    default: _withCtx(() => _cache[6] || (_cache[6] = [
                      _createTextVNode("Save")
                    ])),
                    _: 1,
                    __: [6]
                  }),
                  _createVNode(GgmsButton, {
                    styleType: "simple",
                    classes: ['text-gray-700 font-normal py-0 px-0'],
                    onClick: toggleReschedulePanel
                  }, {
                    default: _withCtx(() => _cache[7] || (_cache[7] = [
                      _createTextVNode("Cancel")
                    ])),
                    _: 1,
                    __: [7]
                  })
                ])
              ])
            ]),
            _: 1
          }, 512),
          (isPreviousActive.value)
            ? (_openBlock(), _createBlock(GgmsButton, {
                key: 0,
                styleType: "primary",
                iconStart: _unref(RewindIcon),
                onClick: goBackToPreviousTask
              }, {
                default: _withCtx(() => _cache[10] || (_cache[10] = [
                  _createTextVNode("Previous ")
                ])),
                _: 1,
                __: [10]
              }, 8, ["iconStart"]))
            : _createCommentVNode("", true),
          (isNextActive.value)
            ? (_openBlock(), _createBlock(GgmsButton, {
                key: 1,
                styleType: "primary",
                iconStart: _unref(ArrowCircleRightIcon),
                onClick: goToNextTask
              }, {
                default: _withCtx(() => _cache[11] || (_cache[11] = [
                  _createTextVNode("Next")
                ])),
                _: 1,
                __: [11]
              }, 8, ["iconStart"]))
            : _createCommentVNode("", true),
          _createVNode(GgmsButton, {
            styleType: "primary",
            iconStart: _unref(CheckCircleIcon),
            onClick: completeTask
          }, {
            default: _withCtx(() => _cache[12] || (_cache[12] = [
              _createTextVNode("Complete")
            ])),
            _: 1,
            __: [12]
          }, 8, ["iconStart"]),
          _createElementVNode("div", _hoisted_9, [
            _cache[13] || (_cache[13] = _createElementVNode("p", { class: "text-sm text-white" }, "Progress", -1)),
            _createElementVNode("p", _hoisted_10, _toDisplayString(activeTaskIndex.value + 1) + "/" + _toDisplayString(activeTasks.value.length), 1)
          ]),
          _createElementVNode("button", {
            class: "bg-primary-color-600 p-2",
            onClick: _cache[4] || (_cache[4] = 
//@ts-ignore
(...args) => (_unref(taskStore).closeTaskSession && _unref(taskStore).closeTaskSession(...args)))
          }, [
            _createVNode(_unref(XIcon), { class: "w-6 h-6 stroke-white" })
          ])
        ])
      ]))
    : _createCommentVNode("", true)
}
}

})